import React, { useState, useEffect, useCallback } from 'react';
import { Select, Spin } from 'antd';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import storeApi from '../../../../services/storeApi';
import timezoneApi from '../../../../services/timezoneApi';
import { SectionHeading } from '../components/settings-ui';

const ALLOWED_TIMEZONE_CITIES = [
  'Adak',
  'Anchorage',
  'Los Angeles',
  'Denver',
  'Chicago',
  'New York',
];

const isAllowedTimezone = (tz) => {
  const haystack = `${tz?.displayName || ''} ${tz?.id || ''}`;
  return ALLOWED_TIMEZONE_CITIES.some((city) => haystack.includes(city));
};

const TimezoneTab = ({ storeDetail, checkUpdate }) => {
  const [loading, setLoading] = useState({
    timezone: false,
    upsert: false,
  });
  const [timezoneList, setTimezoneList] = useState([]);

  const onChangeTimezone = async (val) => {
    const form = {
      ...storeDetail,
      websiteclosedat: storeDetail.websiteclosedat || '',
      timezoneid: val,
    };
    try {
      setLoading((prev) => ({ ...prev, upsert: true }));
      const formData = new FormData();

      for (const key of Object.keys(form)) {
        if (form[key] !== null) {
          formData.append(key, form[key]);
        }
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
      };

      const res = await storeApi.upsert(formData, config);
      if (res.status === 200) {
        checkUpdate(true);
      }
    } catch (error) {
      checkUpdate(false);
    } finally {
      setLoading((prev) => ({ ...prev, upsert: false }));
    }
  };

  const getTimezoneList = useCallback(async () => {
    setLoading((prev) => ({ ...prev, timezone: true }));

    try {
      const list = await timezoneApi.list();
      if (list.data.length > 0) {
        setTimezoneList(list.data.filter(isAllowedTimezone));
      }
    } catch (error) {}
    setLoading((prev) => ({ ...prev, timezone: false }));
  }, []);

  useEffect(() => {
    getTimezoneList();
  }, [getTimezoneList]);

  const currentTimezone = timezoneList.find(
    (tz) => tz.id === (storeDetail.timezoneid || storeDetail.timezone),
  );

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Timezone"
        description="Controls how order times, working hours, and reports are displayed for this store."
      />

      <div className="overflow-hidden rounded-md border border-slate-200 bg-gradient-to-br from-white to-slate-50/70 p-5 shadow-sm dark:border-slate-700 dark:from-slate-900/60 dark:to-slate-900/20">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <GlobeAltIcon className="h-6 w-6" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                Active timezone
              </p>
              <p className="mt-1 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                {currentTimezone?.displayName ||
                  storeDetail.timezone ||
                  'Not set'}
              </p>
            </div>
            <Select
              disabled={loading.timezone || loading.upsert}
              loading={loading.upsert}
              className="store-settings-mobile-select w-full max-w-full sm:w-96"
              size="large"
              placeholder={loading.timezone ? 'Loading…' : 'Select a timezone'}
              value={storeDetail.timezoneid || storeDetail.timezone}
              onChange={(val) => onChangeTimezone(val)}
              showSearch
              optionFilterProp="label"
              notFoundContent={
                loading.timezone ? (
                  <div className="flex items-center justify-center py-4">
                    <Spin size="small" />
                  </div>
                ) : (
                  'No matches'
                )
              }
              options={timezoneList.map((item) => ({
                label: item.displayName,
                value: item.id,
              }))}
            />
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Changes save automatically.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimezoneTab;
